import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FolderOpen, Trash2, Code2, Search, X, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Layout/Navbar';
import './DashboardPage.css';

const formatSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  return (bytes / 1024 / 1024 / 1024).toFixed(2) + ' GB';
};

const timeAgo = (date) => {
  const diff = Date.now() - new Date(date).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const res = await api.get('/files/projects');
      setProjects(res.data.projects);
    } catch { toast.error('Failed to load projects'); }
    finally { setLoading(false); }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.name.endsWith('.zip')) return toast.error('Only ZIP files are allowed');

    const planLimits = { free: 25, basic: 500, pro: 2048, premium: Infinity };
    const limitMB = planLimits[user.plan] || 25;
    if (file.size > limitMB * 1024 * 1024 && limitMB !== Infinity) {
      return toast.error(`File exceeds your plan limit (${limitMB}MB). Upgrade to upload larger files.`);
    }

    setUploading(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Step 1: Upload file to S3 (returns immediately with upload ID)
      const uploadRes = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploadProgress(Math.round(e.loaded / e.total * 100))
      });

      const uploadId = uploadRes.data.uploadId;
      toast.loading('Processing ZIP file...', { id: 'processing' });

      // Step 2: Poll status endpoint until completion
      const projectId = await pollUploadStatus(uploadId);

      if (projectId) {
        toast.success('File extracted successfully! 🎉', { id: 'processing' });
        // Navigate to editor
        setTimeout(() => {
          window.location.href = `/editor/${projectId}`;
        }, 500);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed', { id: 'processing' });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  /**
   * Poll the upload status endpoint until processing is complete
   */
  const pollUploadStatus = async (uploadId) => {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 600; // 30 minutes (3s * 600)
      const pollInterval = 3000; // 3 seconds

      const poll = async () => {
        attempts++;
        try {
          const res = await api.get(`/upload/status/${uploadId}`);
          const { status, result, error } = res.data;

          if (status === 'completed' && result?.projectId) {
            resolve(result.projectId);
          } else if (status === 'failed') {
            reject(new Error(error || 'Upload processing failed'));
          } else if (attempts >= maxAttempts) {
            reject(new Error('Processing timeout'));
          } else {
            setTimeout(poll, pollInterval);
          }
        } catch (err) {
          if (attempts < maxAttempts) {
            setTimeout(poll, pollInterval);
          } else {
            reject(err);
          }
        }
      };

      poll();
    });
  };

  const deleteProject = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this project?')) return;
    try {
      await api.delete(`/files/projects/${id}`);
      setProjects(p => p.filter(pr => pr._id !== id));
      toast.success('Project deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const filtered = projects.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.originalFileName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const planInfo = {
    free: { limit: '25MB', color: '#7a7a9d' },
    basic: { limit: '500MB', color: '#00a3ff' },
    pro: { limit: '2GB', color: '#7c3aed' },
    premium: { limit: 'Unlimited', color: '#00ff88' }
  }[user?.plan] || { limit: '25MB', color: '#7a7a9d' };

  return (
    <div className="dashboard">
      <Navbar />
      <div className="dashboard-body">
        {/* Header */}
        <div className="dash-header">
          <div>
            <h1>Your Projects</h1>
            <p>Upload ZIP files to explore and analyze your code</p>
          </div>
          <div className="dash-plan-badge" style={{ borderColor: planInfo.color + '40', color: planInfo.color }}>
            <span style={{ background: planInfo.color, width: 8, height: 8, borderRadius: '50%', display: 'inline-block' }} />
            {user?.plan?.toUpperCase()} — {planInfo.limit}
          </div>
        </div>

        {/* Upload Zone */}
        <div
          className={`upload-zone ${dragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files[0]); }}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          <input ref={fileInputRef} type="file" accept=".zip" hidden onChange={e => handleUpload(e.target.files[0])} />
          {uploading ? (
            <div className="upload-progress">
              <div className="progress-bar"><div className="progress-fill" style={{ width: uploadProgress + '%' }} /></div>
              <p>Extracting... {uploadProgress}%</p>
            </div>
          ) : (
            <>
              <div className="upload-icon"><Upload size={28} /></div>
              <p className="upload-title">Drop ZIP file here or click to upload</p>
              <p className="upload-sub">Max size: {planInfo.limit} • Only .zip files</p>
            </>
          )}
        </div>

        {/* Search */}
        {projects.length > 0 && (
          <div className="search-bar">
            <Search size={16} className="search-icon" />
            <input className="input" placeholder="Search projects..." style={{ paddingLeft: '40px' }}
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            {searchQuery && <button className="search-clear" onClick={() => setSearchQuery('')}><X size={14} /></button>}
          </div>
        )}

        {/* Projects Grid */}
        {loading ? (
          <div className="projects-loading"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <FolderOpen size={48} />
            <h3>{projects.length === 0 ? 'No projects yet' : 'No results found'}</h3>
            <p>{projects.length === 0 ? 'Upload your first ZIP file to get started' : 'Try a different search term'}</p>
          </div>
        ) : (
          <div className="projects-grid">
            {filtered.map(project => (
              <div key={project._id} className="project-card" onClick={() => navigate(`/editor/${project._id}`)}>
                <div className="project-header">
                  <div className="project-icon"><Code2 size={20} /></div>
                  <button className="project-delete" onClick={e => deleteProject(project._id, e)}>
                    <Trash2 size={14} />
                  </button>
                </div>
                <h3 className="project-name">{project.name}</h3>
                <p className="project-file">{project.originalFileName}</p>
                <div className="project-meta">
                  <span>{project.totalFiles} files</span>
                  <span>{formatSize(project.totalSize)}</span>
                  <span>{timeAgo(project.createdAt)}</span>
                </div>
                <div className={`project-status status-${project.status}`}>{project.status}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
